"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import FieldsFormatter from "@/components/organisms/admin/FieldsFormatter";
import { Button } from "@/components/atoms/Button";
import { validateFields } from "@/lib/utils";
import { toast } from "@/hooks/useToast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import type { FormProps, Section } from "../form4/types";
import {
  DEFAULT_MESSAGES,
  EXCLUDED_PAYLOAD_FIELDS,
  mergeRowWithAdditionalFields,
  buildPayloadWithJsonFields,
} from "../form4/constants";
import { LEADDROP_MOBILE_STRIP_HELPER_TEXT } from "@/constants/admin/fields/leadDropForm/leadDropForm_segmented";

const showToast = {
  success: (message: string) => toast({ title: "Success", description: message }),
  error: (message: string) => toast({ title: "Error", description: message, variant: "destructive" }),
  validation: (title: string, description: string) =>
    toast({ title, description, variant: "destructive" }),
};

type SegmentDef = { id: string; label: string; fields: any[] };

const buildPayload = (fields: Record<string, any>, userId?: number): Record<string, any> => {
  const payload: Record<string, any> = { updated_by: userId };
  Object.entries(fields).forEach(([key, value]) => {
    if (!EXCLUDED_PAYLOAD_FIELDS.has(key)) payload[key] = value;
  });
  return payload;
};

const hasJsonFields = (config: Array<{ isJsonField?: boolean }>) =>
  (config || []).some((f) => f.isJsonField === true);

/**
 * Generic tabbed CMS: multiple segments (tabs), each with its own field list.
 * Configure via variantDetails.segmentDefinitions + variantDetails.componentFields (flattened merge).
 */
const SegmentWorkbenchForm = ({ selectedSection, onSave, variantDetails }: FormProps) => {
  const segments: SegmentDef[] = variantDetails?.segmentDefinitions ?? [];
  const globalSegmentFields: any[] = variantDetails?.globalSegmentFields ?? [];
  const globalLeadDropMobileStripFields: any[] =
    variantDetails?.globalLeadDropMobileStripFields ?? [];
  const mobileStripHelpText =
    variantDetails?.globalLeadDropMobileStripHelpText ?? LEADDROP_MOBILE_STRIP_HELPER_TEXT;

  const showLeadBannerSection =
    globalSegmentFields.length > 0 || globalLeadDropMobileStripFields.length > 0;

  const componentFieldsConfig = variantDetails?.componentFields || [];

  const [activeSegmentId, setActiveSegmentId] = useState<string>("");

  useEffect(() => {
    if (!activeSegmentId && segments[0]?.id) setActiveSegmentId(segments[0].id);
  }, [segments, activeSegmentId]);
  const [componentFields, setComponentFields] = useState<Record<string, any>>({});
  const [componentFieldErrors, setComponentFieldErrors] = useState<Record<string, boolean>>({});
  const [componentFieldMessages, setComponentFieldMessages] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const prevSectionRef = useRef<{ id?: string; componentId?: string; data?: any }>({});
  const dispatch = useAppDispatch();
  const constants = variantDetails?.constants;
  const currentUser = useAppSelector((state) => state.user?.user);
  const reduxState = useAppSelector((state) =>
    variantDetails?.selector ? variantDetails.selector(state.university) : null
  );

  useEffect(() => {
    const hasChanged =
      prevSectionRef.current?.id !== selectedSection.id ||
      prevSectionRef.current?.componentId !== selectedSection?.componentId ||
      prevSectionRef.current?.data !== selectedSection?.data;

    const isInitialMount =
      prevSectionRef.current?.id === selectedSection.id && Object.keys(componentFields).length === 0;

    if (!variantDetails || (!hasChanged && !isInitialMount)) return;

    prevSectionRef.current = {
      id: selectedSection.id,
      componentId: selectedSection?.componentId,
      data: selectedSection?.data,
    };

    const hasData =
      selectedSection?.data &&
      typeof selectedSection.data === "object" &&
      Object.keys(selectedSection.data).length > 0;

    const rawData = hasData ? selectedSection.data : reduxState || {};
    const newFields = hasJsonFields(componentFieldsConfig)
      ? mergeRowWithAdditionalFields(rawData)
      : rawData;
    setComponentFields(newFields);

    if (hasData && variantDetails.setAction) {
      dispatch(variantDetails.setAction(newFields));
    }

    setComponentFieldErrors({});
    setComponentFieldMessages({});
  }, [
    selectedSection.id,
    selectedSection?.componentId,
    selectedSection?.data,
    reduxState,
    variantDetails,
    dispatch,
    componentFieldsConfig,
  ]);

  const handleFieldsUpdate = useCallback(
    (updatedData: any) => {
      setComponentFields((prev) => {
        const next = { ...prev, ...updatedData };
        queueMicrotask(() => {
          if (variantDetails?.setAction) {
            dispatch(
              variantDetails.setAction({
                ...next,
                id: String(next.id ?? ""),
              }),
            );
          }
        });
        return next;
      });
    },
    [variantDetails, dispatch],
  );

  const handleSave = async () => {
    if (!variantDetails || !constants) {
      showToast.error(constants?.CONFIG_NOT_FOUND_ERROR || DEFAULT_MESSAGES.CONFIG_NOT_FOUND);
      return;
    }

    const { errors, messages } = validateFields(componentFieldsConfig, componentFields);
    setComponentFieldErrors(errors);
    setComponentFieldMessages(messages);

    if (Object.keys(errors).length > 0) {
      showToast.validation(
        constants.VALIDATION_ERROR_TITLE || DEFAULT_MESSAGES.VALIDATION_ERROR_TITLE,
        constants.VALIDATION_ERROR_DESCRIPTION || DEFAULT_MESSAGES.VALIDATION_ERROR_DESCRIPTION
      );
      return;
    }

    setIsLoading(true);
    try {
      if (!variantDetails.updateMethod) {
        throw new Error(constants.UPDATE_METHOD_NOT_CONFIGURED_ERROR || DEFAULT_MESSAGES.UPDATE_METHOD_NOT_CONFIGURED);
      }

      const idRaw =
        componentFields.id ??
        (selectedSection?.data &&
        typeof selectedSection.data === "object" &&
        "id" in selectedSection.data
          ? (selectedSection.data as Record<string, unknown>).id
          : undefined);
      if (idRaw == null || idRaw === "") {
        throw new Error(DEFAULT_MESSAGES.ID_REQUIRED);
      }
      const id = Number(idRaw);
      if (!Number.isFinite(id) || id < 1) {
        throw new Error(DEFAULT_MESSAGES.ID_REQUIRED);
      }

      const payload = hasJsonFields(componentFieldsConfig)
        ? buildPayloadWithJsonFields(componentFields, componentFieldsConfig, EXCLUDED_PAYLOAD_FIELDS, currentUser?.id)
        : buildPayload(componentFields, currentUser?.id);

      await variantDetails.updateMethod(id, payload);

      if (variantDetails.setAction) {
        dispatch(variantDetails.setAction({ ...componentFields, id: String(idRaw) }));
      }

      setComponentFields((prev) => ({ ...prev, id: String(idRaw) }));

      const updatedSection: Section = {
        ...selectedSection,
        componentId: String(idRaw),
        data: { ...componentFields, id: String(idRaw) },
      };

      showToast.success(constants.SUCCESS_MESSAGE || DEFAULT_MESSAGES.SUCCESS);
      onSave(updatedSection);
    } catch (error: unknown) {
      console.error("Failed to save:", error);
      const err = error as Error;
      showToast.error(err.message || constants.ERROR_MESSAGE || DEFAULT_MESSAGES.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const activeFields =
    segments.find((s) => s.id === activeSegmentId)?.fields ?? componentFieldsConfig;

  if (!variantDetails) return null;

  const saveButtonText = isLoading
    ? constants?.SAVING_BUTTON_TEXT || DEFAULT_MESSAGES.SAVING_BUTTON
    : constants?.SAVE_BUTTON_TEXT || DEFAULT_MESSAGES.SAVE_BUTTON;

  return (
    <div>
      <div className="mb-6">
        <h3 className="font-semibold mb-2">{variantDetails.name}</h3>
        <p className="text-sm text-gray-500 mb-4">{variantDetails.description}</p>
      </div>

      {showLeadBannerSection && (
        <div className="border border-gray-200 rounded-md p-4 mb-6">
          <p className="text-sm font-medium text-gray-700 mb-3">Lead banner</p>
          {globalSegmentFields.length > 0 ? (
            <FieldsFormatter
              data={componentFields}
              fields={globalSegmentFields}
              updateParent={true}
              onChange={handleFieldsUpdate}
              validationErrors={componentFieldErrors}
              validationMessages={componentFieldMessages}
              className="p-0 bg-transparent"
            />
          ) : null}
          {globalLeadDropMobileStripFields.length > 0 ? (
            <>
              {globalSegmentFields.length > 0 ? (
                <hr className="my-5 border-gray-200" />
              ) : null}
              <p className="text-sm text-gray-600 mb-4 leading-snug">{mobileStripHelpText}</p>
              <FieldsFormatter
                data={componentFields}
                fields={globalLeadDropMobileStripFields}
                updateParent={true}
                onChange={handleFieldsUpdate}
                validationErrors={componentFieldErrors}
                validationMessages={componentFieldMessages}
                className="p-0 bg-transparent"
              />
            </>
          ) : null}
        </div>
      )}

      {segments.length > 0 && (
        <div className="border-b border-gray-200 mb-6">
          <div className="flex flex-wrap gap-1">
            {segments.map((seg) => (
              <button
                key={seg.id}
                type="button"
                onClick={() => setActiveSegmentId(seg.id)}
                className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors ${
                  activeSegmentId === seg.id
                    ? "border-primary-main text-primary-main bg-white"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {seg.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeFields.length > 0 && (
        <div className="border-t pt-6 mb-6">
          <FieldsFormatter
            data={componentFields}
            fields={activeFields}
            updateParent={true}
            onChange={handleFieldsUpdate}
            validationErrors={componentFieldErrors}
            validationMessages={componentFieldMessages}
            className="p-0 bg-transparent"
          />
        </div>
      )}

      <div className="border-t pt-6 mb-6">
        <Button onClick={handleSave} classType="admin" className="w-full" disabled={isLoading}>
          {saveButtonText}
        </Button>
      </div>
    </div>
  );
};

export default SegmentWorkbenchForm;
